<template>
	<aside
		id="card-container"
		class="card-container"
	>
		<PsychologistCard
			v-for="psychologist in psychologistList"
			:key="psychologist.idByAddress"
			:psychologist="psychologist"
			:selected-psychologist-id-by-address="selectedPsychologistIdByAddress"
			@selected-psychologist-id="handleSelectedPsychologist"
		/>
	</aside>
</template>

<script lang="ts">
	import { SearchPsychologist } from '@/types/models/SearchPsychologist';
	import { Vue, Component, Prop, Watch, PropSync } from 'vue-property-decorator';
	import PsychologistCard from '@/components/search-psychologist/result-section/PsychologistCard.vue';
	import { PsychologistService } from '@/services/psychologist-service';

	/**
	 * Liste des psychologues
	 */
	@Component({
		components: { PsychologistCard }
	})
	export default class PsychologistList extends Vue {
		// Liste des psychologues
		@Prop({ default: () => [] })
		readonly psychologistList!: SearchPsychologist[];

		// Id du psychologue sélectionné
		@PropSync('selectedPsychologist', { type: String })
		selectedPsychologistIdByAddress!: string;

		// Taille de l'écran à partir de laquelle on passe en mode horizontal
		readonly BREAK_POINT_WIDTH = 768;

		// Permet de savoir si la sélection provient de la liste des psychologues à gauche de la map
		isFromCard = false;

		/**
		 * Sélectionne le psychologue et émet son id au parent
		 * @param psychologistId Id du psychologue sélectionné
		 */
		handleSelectedPsychologist(psychologistId: string): void {
			this.isFromCard = true;
			this.selectedPsychologistIdByAddress = psychologistId;
			this.$emit('selected-psychologist-id', psychologistId);
		}

		/**
		 * Scroll jusqu'au psychologue sélectionné
		 */
		@Watch('selectedPsychologistIdByAddress')
		handlePsychologistIdChange(): void {
			this.scrollTo();
		}

		/**
		 * Si une nouvelle recherche est effectuée, on réinitialise le scroll au premier psychologue
		 */
		@Watch('psychologistList')
		handlePsychologistListChange(): void {
			this.$nextTick(() => {
				this.resetScroll();
			});
		}

		/**
		 * Récupère la liste des psychologues ayant la même adresse
		 *
		 * @returns Liste des psychologues ayant la même adresse
		 */
		getPsychologistListWithSameAddress(): SearchPsychologist[] {
			return this.psychologistList.filter(psychologist =>
				PsychologistService.getCoordinatesFromPsychologistIdByAddress(psychologist.idByAddress) === PsychologistService.getCoordinatesFromPsychologistIdByAddress(this.selectedPsychologistIdByAddress));
		}

		/**
		 * Scroll jusqu'au psychologue sélectionné
		 */
		scrollTo(): void {
			let tempPsychologistId = this.selectedPsychologistIdByAddress;

			// Si la sélection provient de la liste des psychologues à gauche de la map, on scroll jusqu'au psychologue sélectionné
			if (!this.isFromCard) {
				// Si la sélection provient de la map, on va vérifier s'il y a plusieurs psychologues avec la même adresse
				const psychologistList = this.getPsychologistListWithSameAddress();
				// Si plusieurs psychologues ont la même adresse, on scroll jusqu'au premier psychologue trouvé
				if (psychologistList.length > 1) {
					tempPsychologistId = psychologistList[0].idByAddress;
					this.selectedPsychologistIdByAddress = PsychologistService.getCoordinatesFromPsychologistIdByAddress(psychologistList[0].idByAddress);
				}
			}

			const cardContainer = document.getElementById('card-container');
			if (cardContainer) {
				const card = document.getElementById(`card-${tempPsychologistId}`);
				if (card) {
					if (window.innerWidth > this.BREAK_POINT_WIDTH) {
						this.scrollToVertical(cardContainer, card);
					} else {
						this.scrollToHorizontal(cardContainer, card);
					}
				}
			}

			this.isFromCard = false;
		}

		/**
		 * Scroll jusqu'au psychologue sélectionné en mode vertical
		 * @param container Conteneur de la liste
		 * @param card Carte du psychologue sélectionné
		 */
		scrollToVertical(container: HTMLElement, card: HTMLElement): void {
			const { containerRect, cardRect } = this.getHtmlElementDetails(container, card);
			const offsetTop = cardRect.top - containerRect.top;

			container.scrollTo({
				top: container.scrollTop + offsetTop,
				behavior: 'smooth'
			});
		}

		/**
		 * Scroll jusqu'au psychologue sélectionné en mode horizontal
		 * @param container Conteneur de la liste
		 * @param card Carte du psychologue sélectionné
		 */
		scrollToHorizontal(container: HTMLElement, card: HTMLElement): void {
			const { containerRect, cardRect } = this.getHtmlElementDetails(container, card);
			const offsetLeft = cardRect.left - containerRect.left;

			container.scrollTo({
				left: container.scrollLeft + offsetLeft,
				behavior: 'smooth'
			});
		}

		/**
		 * Récupère les éléments HTML de la liste et de la carte du psychologue sélectionné
		 * @param container Conteneur de la liste
		 * @param card Carte du psychologue sélectionné
		 */
		getHtmlElementDetails(container: HTMLElement, card: HTMLElement): { containerRect: DOMRect; cardRect: DOMRect } {
			const containerRect = container.getBoundingClientRect();
			const cardRect = card.getBoundingClientRect();

			return { containerRect, cardRect };
		}

		/**
		 * Réinitialise le scroll au premier psychologue
		 */
		resetScroll(): void {
			const cardContainer = document.getElementById('card-container');
			if (cardContainer) {
				if (window.innerWidth > this.BREAK_POINT_WIDTH) {
					cardContainer.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				} else {
					cardContainer.scrollTo({
						left: 0,
						behavior: 'smooth'
					});
				}
			}
		}
	}
</script>

<style scoped lang="css">
	.card-container {
		width: 100%;
		display: grid;
		border-width: 1px 0 1px 0;
		border-color: #eee;
		border-style: solid;
		gap: 1rem;
		overflow: auto;
		scroll-snap-type: y mandatory;
	}

	@media screen and (max-width: 768px) {
		.card-container {
			display: flex;
			overflow-y: hidden;
			scroll-snap-type: x mandatory;
			border: none;
		}
	}
</style>
